/*
 * Handles communication between windows
 */
import objectUtils from "@/common/utils/objectUtils";
import stringUtils from "@/common/utils/stringUtils";
import type { LegacyMessage } from "@/types/messages";

// A window, or an iframe element when its contentWindow is not there yet.
export type MessageTarget = Window | HTMLIFrameElement;

export type MessengerCallback<T = unknown> = (
  messenger: Messenger<T>,
  event: MessageEvent,
  data: LegacyMessage,
) => void;

/** Handlers keyed `on<CamelType>`, e.g. `onMediaReady` for `media_ready`. */
export type MessengerHandlers<T = unknown> = Record<
  string,
  MessengerCallback<T>
>;

/** `T` is the user data type; it is null until setUserData. */
export default class Messenger<T = unknown> {
  declare window: Window;
  declare targetOrigin: string;
  declare onMessengerHandler: MessengerHandlers<T> | null;
  declare targetWindow: MessageTarget | null;
  declare userData: T | null;
  declare prefix: string;
  declare nextId: number;
  declare name: string;

  /*
   * _window is the window to listen on.
   * targetWindow is the window to communicate with.
   * targetOorigin is the origin url of the target.
   */
  constructor(
    _window?: Window | null,
    targetWindow?: MessageTarget | null,
    targetOrigin?: string | null,
  ) {
    this.window = _window || window;
    this._onHandleMessage = this._onHandleMessage.bind(this);

    this.targetOrigin = targetOrigin || "*";
    this.onMessengerHandler = null;
    this.targetWindow = targetWindow || null;
    this.userData = null;
    this.prefix = "";
    this.nextId = 0;
    this.name = "messenger";
  }

  /*
   * Handles a message form another window.
   */
  _onHandleMessage(event: MessageEvent) {
    if (
      objectUtils.isString(event.data) === false ||
      (this.prefix && !event.data.startsWith(this.prefix))
    ) {
      return;
    }

    const json = this.prefix
      ? event.data.substring(this.prefix.length)
      : event.data;

    const data: LegacyMessage = JSON.parse(json);

    if (data.type) {
      const name = `on${stringUtils.toCamelCase(data.type, "_", "", true)}`;
      if (
        this.onMessengerHandler &&
        name in this.onMessengerHandler &&
        typeof this.onMessengerHandler[name] === "function"
      ) {
        this.onMessengerHandler[name](this, event, data);
      }
    }
  }

  _postMessage(message: Record<string, unknown>) {
    if (
      this.targetWindow &&
      (this.targetWindow as Window).postMessage &&
      this.targetOrigin
    ) {
      const messageString = this.prefix + JSON.stringify(message);

      (this.targetWindow as Window).postMessage(
        messageString,
        this.targetOrigin,
      );
    }
  }

  postMessage(type: string, data?: Record<string, unknown>) {
    const message = objectUtils.extend({}, data);
    message.type = type;
    message.messageid = `${this.name || "messenger"}-${this.nextId++}`;
    this._postMessage(message);
  }

  setName(name: string) {
    this.name = name;
  }

  /*
   * The prefix allows a tag to be added as a prefix to a sent message. Upon
   * receiving the message the message is checked to ensure the message starts
   * with prefix. Prefix is stripped before parsing the message a json. This
   * ensures that we only parse the proper messages.
   */
  setPrefix(prefix: string) {
    this.prefix = prefix;
  }

  setUserData(udata: T) {
    this.userData = udata;
  }

  getUserData(): T | null {
    return this.userData;
  }

  startListening() {
    this.window.addEventListener("message", this._onHandleMessage, false);
  }

  stopListening() {
    this.window.removeEventListener("message", this._onHandleMessage, false);
  }

  setTargetOrigin(url: string) {
    this.targetOrigin = url;
  }
}
